import { parsePhoneNumberFromString } from "libphonenumber-js/max";

export interface CustomerContact {
  contactType?: "phone" | "whatsapp";
  telephone?: string | null;
  unifiedTelephone?: string | null;
  whatsappUsername?: string | null;
  default?: boolean;
  primary?: boolean;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

export function normalizeContactPhone(
  value?: string | null,
): string | undefined {
  if (typeof value !== "string" || !value || !/^[+\d\s().-]+$/.test(value))
    return undefined;
  const input = value.trim().replace(/^00/, "+");
  const digits = input.replace(/[\s().-]/g, "");
  // Preserve the CRM's known Egyptian mobile format without assigning Egypt to
  // ambiguous local landlines or foreign numbers. International values carry their own country.
  const candidate = /^01[0125]\d{8}$/.test(digits)
    ? `+20${digits.slice(1)}`
    : digits.startsWith("+")
      ? digits
      : `+${digits}`;
  const parsed = parsePhoneNumberFromString(candidate);
  if (parsed?.isValid()) return parsed.number;
  return undefined;
}

/** Explicit selection must never fall through to a different stored number. */
export function resolveSelectedContactPhone(
  selected: string | null | undefined,
  contacts?: readonly CustomerContact[] | null,
  hidden = false,
) {
  if (hidden) return undefined;
  return selected === undefined
    ? resolveCustomerContact(contacts).phone
    : normalizeContactPhone(selected);
}

export function buildTruecallerLink(value?: string | null) {
  const phone = normalizeContactPhone(value);
  const country = phone && parsePhoneNumberFromString(phone)?.country;
  return phone && country
    ? `https://truecaller.com/search/${country.toLowerCase()}/${encodeURIComponent(phone)}`
    : undefined;
}

export function normalizeWhatsappUsername(
  value?: string | null,
): string | undefined {
  const username =
    typeof value === "string" ? value.trim().replace(/^@/, "") : undefined;
  return username &&
    !["null", "undefined"].includes(username.toLowerCase()) &&
    /^(?=.{3,35}$)(?=.*[A-Za-z])[A-Za-z0-9._]+$/.test(username)
    ? username
    : undefined;
}

export function resolveCustomerContact(
  contacts?: readonly CustomerContact[] | null,
  hidden = false,
) {
  const empty = {
    kind: hidden ? "hidden" : "empty",
    label: hidden ? "Hidden" : "No contact details",
    phone: undefined,
    username: undefined,
  } as const;
  if (hidden) return empty;
  const active = (contacts ?? []).filter((contact) => !contact.deletedAt);
  const phones = active.filter(
    (contact) =>
      normalizeContactPhone(contact.unifiedTelephone) ||
      normalizeContactPhone(contact.telephone),
  );
  const selected =
    phones.find((contact) => contact.default || contact.primary) ?? phones[0];
  if (selected) {
    const phone =
      normalizeContactPhone(selected.unifiedTelephone) ||
      normalizeContactPhone(selected.telephone);
    return {
      kind: "phone" as const,
      label:
        normalizeContactPhone(selected.telephone) === phone
          ? selected.telephone!
          : phone!,
      phone,
      username: undefined,
    };
  }
  const whatsapp = active
    .filter((contact) => normalizeWhatsappUsername(contact.whatsappUsername))
    .sort(
      (a, b) =>
        (Date.parse(b.updatedAt || "") || 0) -
        (Date.parse(a.updatedAt || "") || 0),
    )[0];
  const username = normalizeWhatsappUsername(whatsapp?.whatsappUsername);
  if (username)
    return {
      kind: "whatsapp" as const,
      label: `@${username}`,
      phone: undefined,
      username,
    };
  const rawPhones = active.filter((contact) => {
    const value = contact.telephone?.trim() || contact.unifiedTelephone?.trim();
    return value && !["null", "undefined"].includes(value.toLowerCase());
  });
  const raw =
    rawPhones.find((contact) => contact.default || contact.primary) ??
    rawPhones[0];
  return raw
    ? {
        kind: "unverified-phone" as const,
        label: (raw.telephone?.trim() || raw.unifiedTelephone?.trim())!,
        phone: undefined,
        username: undefined,
      }
    : empty;
}

export function buildWhatsappLink(
  contact: { phone?: string | null; username?: string | null },
  message?: string,
): string | undefined {
  const phone = normalizeContactPhone(contact.phone);
  const username = normalizeWhatsappUsername(contact.username);
  const recipient = phone?.replace(/^\+/, "") || username;
  if (!recipient) return undefined;
  return `https://wa.me/${encodeURIComponent(recipient)}${message ? `?text=${encodeURIComponent(message)}` : ""}`;
}
